import type { MutableRefObject } from "react";
import styles from "../../MarkdownContent.module.scss";

interface IExpandedImage {
  src: string;
  alt: string;
}

interface IExpandedImagePan {
  x: number;
  y: number;
}

interface IExpandedImageDragState {
  startX: number;
  startY: number;
  baseX: number;
  baseY: number;
}

interface IImageExpandedRendererProps {
  expandedImage: IExpandedImage;
  isExpandedImageZoomed: boolean;
  isExpandedImageDragging: boolean;
  expandedImagePan: IExpandedImagePan;
  expandedImageDidMoveRef: MutableRefObject<boolean>;
  expandedImageDragStateRef: MutableRefObject<IExpandedImageDragState>;
  closeExpandedImage: () => void;
  setIsExpandedImageZoomed: (value: boolean) => void;
  setIsExpandedImageDragging: (value: boolean) => void;
  setExpandedImagePan: (value: IExpandedImagePan) => void;
}

export function ImageExpandedRenderer({
  expandedImage,
  isExpandedImageZoomed,
  isExpandedImageDragging,
  expandedImagePan,
  expandedImageDidMoveRef,
  expandedImageDragStateRef,
  closeExpandedImage,
  setIsExpandedImageZoomed,
  setIsExpandedImageDragging,
  setExpandedImagePan,
}: IImageExpandedRendererProps): React.JSX.Element {
  return (
    <div className={styles.expandedImageOverlay}>
      <button
        type="button"
        className={styles.expandedImageBackdrop}
        aria-label="Close enlarged image overlay"
        onClick={closeExpandedImage}
      />
      <div
        className={styles.expandedImageDialog}
        role="dialog"
        aria-modal="true"
        aria-label="Expanded image preview"
      >
        <div className={styles.expandedImageViewport}>
          <button
            type="button"
            aria-label={isExpandedImageZoomed ? "Zoom out image" : "Zoom in image"}
            className={`${styles.expandedImageInteractionButton} ${isExpandedImageZoomed ? styles.expandedImageZoomed : styles.expandedImageZoomable} ${isExpandedImageDragging ? styles.expandedImageDragging : ""}`.trim()}
            onClick={(): void => {
              if (expandedImageDidMoveRef.current) {
                expandedImageDidMoveRef.current = false;
                return;
              }

              if (isExpandedImageZoomed) {
                setIsExpandedImageZoomed(false);
                setIsExpandedImageDragging(false);
                setExpandedImagePan({ x: 0, y: 0 });
                return;
              }

              setIsExpandedImageZoomed(true);
            }}
            onMouseDown={(event): void => {
              if (!isExpandedImageZoomed) {
                return;
              }

              event.preventDefault();
              expandedImageDidMoveRef.current = false;
              setIsExpandedImageDragging(true);
              expandedImageDragStateRef.current = {
                startX: event.clientX,
                startY: event.clientY,
                baseX: expandedImagePan.x,
                baseY: expandedImagePan.y,
              };
            }}
            onMouseMove={(event): void => {
              if (!isExpandedImageZoomed || !isExpandedImageDragging) {
                return;
              }

              const deltaX = event.clientX - expandedImageDragStateRef.current.startX;
              const deltaY = event.clientY - expandedImageDragStateRef.current.startY;
              if (Math.abs(deltaX) > 1 || Math.abs(deltaY) > 1) {
                expandedImageDidMoveRef.current = true;
              }

              setExpandedImagePan({
                x: expandedImageDragStateRef.current.baseX + deltaX,
                y: expandedImageDragStateRef.current.baseY + deltaY,
              });
            }}
            onMouseUp={(): void => {
              if (isExpandedImageDragging) {
                setIsExpandedImageDragging(false);
              }
            }}
            onMouseLeave={(): void => {
              if (isExpandedImageDragging) {
                setIsExpandedImageDragging(false);
              }
            }}
          >
            <img
              className={styles.expandedImage}
              src={expandedImage.src}
              alt={expandedImage.alt}
              draggable={false}
              style={isExpandedImageZoomed ? { transform: `translate(${expandedImagePan.x}px, ${expandedImagePan.y}px) scale(2)` } : undefined}
            />
          </button>
        </div>
      </div>
    </div>
  );
}

